import { doEncrypt, doCrypt } from '@/utils/cryptForRefresh'
import jwtDecode from 'jwt-decode'
import { authHost } from '@/config'
import { getValue, getMainDomain } from '@/utils/helpers'
import { setCookie, getCookie } from '@/utils/cookie'

const cookieRedirectName = 'user_redirect_url'

class User {
  constructor () {
    this.userAccessToken = null
    this.userRefreshToken = null
    this.relativePrefix = ''
  }

  getDomainWithDot () {
    return window.location.hostname === 'localhost'
      ? 'localhost'
      : '.' + getMainDomain(window.location.host.split(':')[0])
  }

  // добавляет относительный путь к запросам
  // нужно для проекта ГИСП
  setRelativePrefix (prefix = '') {
    this.relativePrefix = prefix
  }

  // сохраним токены (для локальной работы)
  setUserTokens ({ userAccessToken, userRefreshToken }) {
    this.userAccessToken = userAccessToken
    this.userRefreshToken = userRefreshToken

    // токен авторизации для пользователя
    localStorage.setItem('user-access-token', userAccessToken)
    localStorage.setItem('user-refresh-token', doCrypt(userRefreshToken))

    const cookieOptions = {
      path: '/',
      expires: 60 * 60 * 24 * 30, // на 30 дней
    }

    cookieOptions.domain = window.location.host.split(':')[0].replace('www.', '.')

    setCookie('access_token', userAccessToken, cookieOptions)
  }

  // достаем токены из local storage (для локальной работы)
  restoreTokens () {
    const accessToken = localStorage.getItem('user-access-token')
    const refreshToken = localStorage.getItem('user-refresh-token')

    if (accessToken && accessToken !== 'null' && accessToken !== 'undefined') {
      this.userAccessToken = accessToken || null
    }

    if (refreshToken && refreshToken !== 'null' && refreshToken !== 'undefined') {
      try {
        this.userRefreshToken = doEncrypt(refreshToken) || null
      } catch (e) {
        console.log('restoreTokens', e)
      }
    }
  }

  // выход
  logoutUser (appEnv = 'prod') {
    localStorage.removeItem('user-access-token')
    localStorage.removeItem('user-refresh-token')
    localStorage.removeItem('current-company-name')
    localStorage.removeItem('user-data')

    this.userAccessToken = null
    this.userRefreshToken = null

    setCookie('access_token', '', {
      expires: 1,
      domain: this.getDomainWithDot(),
    })

    setCookie('access_token', '', {
      expires: 1,
      domain: this.getDomainWithDot(),
    })

    const passportLogout = (resolve) => {
      const host = authHost(appEnv)

      fetch(`${host}/logout`, {
        credentials: 'include',
      })
        .then(resolve)
        .catch(resolve)
    }

    return new Promise((resolve) => {
      fetch(`${this.relativePrefix}/api/logout`)
        .then(() => passportLogout(resolve))
        .catch(() => passportLogout(resolve))
    })
  }

  // запрашиваем данные пользователя
  fetchContext () {
    const userAccessToken = this.userAccessToken
    const headers = {}

    if (userAccessToken) {
      headers.Authorization = `Bearer ${userAccessToken}`
    }

    return fetch(`${this.relativePrefix}/api/passport/users/me`, {
      method: 'GET',
      headers,
    })
      .then((response) => {
        if (response.status !== 200) {
          throw response
        }

        return response.json()
      })
      .then((res) => {
        localStorage.setItem('UUID', res.id)
        return res
      })
      .catch((err) => {
        throw err
      })
  }

  // получить данные пользователя по токену (который хранится в куках)
  fetchContextByToken () {
    const userAccessToken = this.userAccessToken
    const headers = {}

    if (userAccessToken) {
      headers.Authorization = `Bearer ${userAccessToken}`
    }

    return fetch(`${this.relativePrefix}/api/users/token`, {
      method: 'GET',
      headers,
    })
      .then((response) => {
        if (response.status !== 200) {
          throw response
        }

        return response.json()
      })
      .then((res) => {
        localStorage.setItem('UUID', res.id)
        return res
      })
      .catch((err) => {
        throw err
      })
  }

  /* eslint-disable */
  // получаем контекст пользователя и возвращаем на исходную страницу
  fetchContextWithRedirect() {
    return this.fetchContext().then((res) => {
      if (!getValue(res, "context.registered", false) && res.id) {
        if (window.location.pathname !== "/validate") window.location.href = "/validate";
        return res;
      }

      this.redirectAfterAuth();

      return res;
    });
  }

  redirectAfterAuth() {
    const redirectUrlAfterAuth = getCookie(cookieRedirectName);

    if (redirectUrlAfterAuth) {
      setCookie(cookieRedirectName, "", {
        path: "/",
        expires: 1,
        domain: this.getDomainWithDot(),
      });

      if (redirectUrlAfterAuth !== window.location.href) {
        window.location.href = redirectUrlAfterAuth;
        return true;
      }
    }

    return false;
  }

  // проверям авторизован я в сервисе auth.bimlib.pro
  // если авторизован - можно автоматически залогиниться
  isAuthorized(appEnv = "prod") {
    const host = authHost(appEnv);

    return new Promise((resolve, reject) => {
      fetch(`${host}/login/success`, {
        method: "GET",
        credentials: "include",
        redirect: "manual",
      })
        .then((response) => {
          if (response.status === 200) {
            resolve(true);
            return;
          }

          if (response.redirected) {
            // eslint-disable-next-line prefer-promise-reject-errors
            reject(false);
            return;
          }

          // eslint-disable-next-line prefer-promise-reject-errors
          reject(false);
        })
        .catch(() => {
          // eslint-disable-next-line prefer-promise-reject-errors
          reject(false);
        });
    });
  }

  // загрузка изображений
  async uploadImage(file) {
    const userAccessToken = this.userAccessToken;
    const headers = {};

    if (userAccessToken) {
      headers.Authorization = `Bearer ${userAccessToken}`;
    }

    headers.Accept = "application/json";

    const formData = new FormData();
    formData.append("file", file, file.name);

    const uploadResult = await fetch(`${this.relativePrefix}/api/assets/images`, {
      method: "POST",
      headers,
      body: formData,
    }).then((r) => r.json());

    return uploadResult;
  }

  // изменить пароль
  /*
   * data = { validationToken, newPassword, passwordConfirm}
   */
  async changePassword(data) {
    const userAccessToken = this.userAccessToken;
    const headers = {};

    if (userAccessToken) {
      headers.Authorization = `Bearer ${userAccessToken}`;
    }

    headers.Accept = "application/json";
    headers["Content-Type"] = "application/json";

    return fetch(`${this.relativePrefix}/api/passport/users/me/password`, {
      method: "PUT",
      headers,
      body: JSON.stringify(data),
    }).then((r) => r.json());
  }

  // создать компанию пользователю
  async createCompany({
    nameShort,
    registrationNumber, // ОГРН
    inn, // inn
    position,
    categoryCode,
  }) {
    const data = {
      company: {
        nameShort,
      },
      owner: true,
      position,
    };

    if (registrationNumber) {
      data.company.registrationNumber = registrationNumber;
    }

    if (categoryCode) {
      data.company.categoryCode = categoryCode;
    }

    if (inn) {
      data.company.registrationInfo = {
        inn: {
          number: inn,
          date: null,
        },
      };
    }

    const userAccessToken = this.userAccessToken;
    const headers = {};

    if (userAccessToken) {
      headers.Authorization = `Bearer ${userAccessToken}`;
    }

    headers.Accept = "application/json";
    headers["Content-Type"] = "application/json";

    const result = await fetch(`${this.relativePrefix}/api/passport/users/me/employments`, {
      method: "POST",
      headers,
      body: JSON.stringify(data),
    }).then((r) => r.json());

    return result;
  }

  /** Получение телефона */
  async getPhone() {
    const headers = {
      Accept: "application/json",
    };

    if (this.userAccessToken) {
      headers.Authorization = `Bearer ${this.userAccessToken}`;
    }

    try {
      const resp = await fetch(`${this.relativePrefix}/api/passport/users/me/phone`).then((r) =>
        r.json()
      );
      return resp;
    } catch (e) {
      return {};
    }
  }

  /** Обновление телефона пользователя */
  async updatePhone(phone) {
    const headers = {
      Accept: "application/json",
      "Content-Type": "text/plain",
    };

    if (this.userAccessToken) {
      headers.Authorization = `Bearer ${this.userAccessToken}`;
    }

    const resp = await fetch(`${this.relativePrefix}/api/passport/users/me/phone`, {
      method: "PUT",
      headers,
      body: phone,
    }).then((r) => r.json());

    return resp;
  }

  /** Получение кода по СМС для верификации */
  // принимаем токен капчи
  async validatePhoneRequest(recaptchaToken) {
    const headers = {
      Accept: "application/json",
    };

    if (this.userAccessToken) {
      headers.Authorization = `Bearer ${this.userAccessToken}`;
    }

    // передаем в query капчу
    const resp = await fetch(
      `${this.relativePrefix}/api/passport/users/me/phone/validate/request?token=${recaptchaToken}`,
      {
        method: "PUT",
        headers,
      }
    )
      .then((r) => {
        return r.json();
      })
      .catch((e) => {
        return e;
      });

    return resp;
  }

  /** Ввод кода из СМС для верификации */
  validatePhone(code) {
    const headers = {
      Accept: "application/json",
      "Content-Type": "text/plain",
    };

    if (this.userAccessToken) {
      headers.Authorization = `Bearer ${this.userAccessToken}`;
    }

    return fetch(`${this.relativePrefix}/api/passport/users/me/phone/validate`, {
      method: "PUT",
      headers,
      body: code,
    });
  }

  deletePhone() {
    const userAccessToken = this.userAccessToken;
    const headers = {};

    if (userAccessToken) {
      headers.Authorization = `Bearer ${userAccessToken}`;
    }

    return fetch(`${this.relativePrefix}/api/passport/users/me/phone`, {
      method: "DELETE",
    });
  }

  // обновить контекст пользователя
  async updateContext(context, method = "PUT") {
    const userAccessToken = this.userAccessToken;
    const headers = {};

    if (userAccessToken) {
      headers.Authorization = `Bearer ${userAccessToken}`;
    }

    headers.Accept = "application/json";
    headers["Content-Type"] = "application/json";

    // обновим профиль
    await fetch(`${this.relativePrefix}/api/passport/users/me`, {
      method,
      headers,
      body: JSON.stringify(context),
    }).then((response) => response.json());

    // обновим access_token токены
    await this.refreshAccessToken();

    // получим новый профиль
    const newContext = await this.fetchContext();

    return newContext;
  }

  /* обновление access_token */
  async refreshAccessToken() {
    const userAccessToken = this.userAccessToken;
    const headers = {};

    if (userAccessToken) {
      headers.Authorization = `Bearer ${userAccessToken}`;
    }

    headers.Accept = "application/json";
    headers["Content-Type"] = "application/json";

    try {
      await fetch(`${this.relativePrefix}/refresh_access_token`, {
        method: "POST",
        headers,
      }).then((response) => response.json());
    } catch (e) {}
  }

  // установить активную компанию
  async setCompany(companyId) {
    const userAccessToken = this.userAccessToken;
    const headers = {};
    const data = { id: companyId };

    if (userAccessToken) {
      headers.Authorization = `Bearer ${userAccessToken}`;
    }

    headers.Accept = "application/json";
    headers["Content-Type"] = "application/json";

    // обновим текущую компанию
    await fetch(`${this.relativePrefix}/api/passport/users/me/company`, {
      method: "PUT",
      headers,
      body: JSON.stringify(data),
    }).then((response) => response.json());

    // обновим access_token токены
    await this.refreshAccessToken();

    // получим новый профиль
    const newContext = await this.fetchContext();

    return newContext;
  }

  // сбросить активную компанию
  async resetCompany() {
    const userAccessToken = this.userAccessToken;
    const headers = {};

    if (userAccessToken) {
      headers.Authorization = `Bearer ${userAccessToken}`;
    }

    headers.Accept = "application/json";
    headers["Content-Type"] = "application/json";

    // сбросим компанию
    await fetch(`${this.relativePrefix}/api/passport/users/me/company`, {
      method: "DELETE",
      headers,
      body: "",
    });

    // обновим access_token токены
    await this.refreshAccessToken();

    // получим новый профиль
    const newContext = await this.fetchContext();

    return newContext;
  }

  // установить пользовательские параметры (любые)
  async setParams(params = {}, { fetchContextAfter = true } = {}) {
    const userAccessToken = this.userAccessToken;
    const headers = {};

    if (userAccessToken) {
      headers.Authorization = `Bearer ${userAccessToken}`;
    }

    headers.Accept = "application/json";
    headers["Content-Type"] = "application/json";

    // обновим текущую компанию
    await fetch(`${this.relativePrefix}/api/passport/users/me/context`, {
      method: "PUT",
      headers,
      body: JSON.stringify(params),
    }).then((response) => response.json());

    if (fetchContextAfter) {
      // получим новый профиль
      const newContext = await this.fetchContext();
      return newContext;
    }
  }

  /* регистрация пользователя */
  // принимаем новым аргументом капчу
  signUp({ user, service }, recaptchaToken) {
    // передаем в query капчу
    let url = `${this.relativePrefix}/api/passport/users?token=${recaptchaToken}`;

    if (service) {
      url += `&service=${service}`;
    }

    return fetch(url, {
      method: "POST",
      body: JSON.stringify(user),
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    }).then((r) => {
      return r.json();
    });
  }

  // получим токен по auth_code
  fetchTokenByAuthCode(appEnv = "prod") {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get("code");
    const host = authHost(appEnv);

    const formData = new FormData();
    formData.append("grant_type", "authorization_code");
    formData.append("redirect_uri", `${window.location.origin}/oauth/code`);
    formData.append("code", code);

    return fetch(`${host}/oauth/token`, {
      method: "POST",
      headers: {
        Authorization: "Basic d2ViOkF3RWhGTFFxN2pFSkhBS0RLbUNqcnhZNkFGUTlwVUdX",
      },
      body: formData,
    })
      .then(async (res) => {
        if (res.status !== 200) {
          throw res;
        }

        const result = await res.json();

        if (result.access_token && result.refresh_token) {
          this.setUserTokens({
            userAccessToken: result.access_token,
            userRefreshToken: result.refresh_token,
          });
        }

        return result;
      })
      .catch((err) => {
        throw err;
      });
  }

  // создадим сессию
  // emailToken - токен который пришел из письма регистрации
  createSession(appEnv = "prod", emailToken) {
    const host = authHost(appEnv);

    return fetch(`${host}/token`, {
      method: "GET",
      credentials: "include",
      headers: {
        Authorization: `Bearer ${emailToken}`,
      },
    })
      .then((res) => {
        if (res.status !== 200) {
          throw res;
        }

        return true;
      })
      .catch((err) => {
        throw err;
      });
  }

  // открыть страницу авторизации
  openLoginPage(clientId = "web", appEnv = "prod", returnUrl = null) {
    const host = authHost(appEnv);
    const redirectUri = `${window.location.origin}${this.relativePrefix}/oauth/code`;

    if (returnUrl !== false) {
      setCookie(cookieRedirectName, returnUrl || window.location.href, {
        path: "/",
        domain: this.getDomainWithDot(),
      });
    }

    window.location = `${host}/oauth/authorize?response_type=code&client_id=${clientId}&scope=all&redirect_uri=${redirectUri}`;
  }

  // запрос на токен - получение и рефреш для всех типов
  tokenRequest(payload) {
    const formData = new FormData();
    const keys = Object.keys(payload);

    keys.forEach((key) => {
      formData.append(key, payload[key]);
    });

    return fetch(`${this.relativePrefix}/api/passport/oauth/token`, {
      method: "POST",
      headers: {
        Authorization: "Basic " + btoa("web:AwEhFLQq7jEJHAKDKmCjrxY6AFQ9pUGW"),
      },
      body: formData,
    }).then((response) => response.json());
  }

  // обновим токены - если они протухли (для локальной работы)
  refreshTokens(forceUpdate = false) {
    // запрос на рефреш токен - всех типов
    const refreshToken = (refreshToken) => {
      // eslint-disable-next-line no-async-promise-executor
      return new Promise(async (resolve) => {
        try {
          const payload = {
            grant_type: "refresh_token",
            refresh_token: refreshToken,
          };

          const res = await this.tokenRequest(payload);

          resolve({
            error: null,
            accessToken: res.access_token,
            refreshToken: res.refresh_token,
          });
        } catch (e) {
          console.log(e);

          resolve({ error: "error refresh token" });
        }
      });
    };

    // eslint-disable-next-line no-async-promise-executor
    return new Promise(async (resolve) => {
      const { userAccessToken, userRefreshToken } = this;

      const currentTimestamp = Math.round(Date.now() / 1000) + 1 * 60 * 60; // добавим еще 1 час - чуть ранее рефрешить

      // проверим что токен живой
      if (userAccessToken || userRefreshToken) {
        let needRefreshUserAccess = false;

        if (userAccessToken) {
          const jwtUserAccess = jwtDecode(userAccessToken);

          if (jwtUserAccess.exp < currentTimestamp) {
            needRefreshUserAccess = true;
          }
        } else {
          needRefreshUserAccess = true;
        }

        // запросим токен
        if ((needRefreshUserAccess || forceUpdate) && userRefreshToken) {
          const userTokens = await refreshToken(userRefreshToken);

          if (!userTokens.error) {
            this.setUserTokens({
              userAccessToken: userTokens.accessToken,
              userRefreshToken: userTokens.refreshToken,
            });
          }
        }
      }

      resolve();
    });
  }

  // получить текущую компанию
  contextActiveCompany(context) {
    const currentCompanyId = getValue(context, "currentCompany.id", null);
    const employments = getValue(context, "employments", []);
    const employment = employments.find((employment) => {
      return employment.company && employment.company.id === currentCompanyId;
    });

    return employment ? employment.company : null;
  }

  // получить пермишены пользователя
  contextPermissions(context) {
    const activeCompany = this.contextActiveCompany(context);

    if (!activeCompany) {
      return getValue(context, "permissions", []);
    }

    const currentCompanyId = activeCompany.id;
    const employments = getValue(context, "employments", []);
    const employment = employments.find((employment) => employment.company.id === currentCompanyId);

    if (!employment) {
      return getValue(context, "permissions", []);
    }

    return getValue(employment, "permissions", []);
  }

  // получить пермишены для конкретного модуля
  contextModulePermissions(context, module) {
    const myPermissions = this.contextPermissions(context);
    return myPermissions.find((item) => item.name === module);
  }
}

export default {
  install(Vue) {
    const user = new User();

    if (typeof localStorage !== "undefined") {
      user.restoreTokens();
    }

    Vue.prototype.$user = user;
  },
};
